import { inject, Injectable, signal } from '@angular/core';
import ConnectWallet, { Link, LinkSession, ProtonWebLink } from '@proton/web-sdk';
import { environment } from '../../../environments/environment';
import { JSON_RPC } from '../../app.config';
import {
  MAINNET_CHAIN_ID,
  MAINNET_ENDPOINTS,
  TESTNET_CHAIN_ID,
  TESTNET_ENDPOINTS,
} from '../../constants/blockchain.constants';
import { deserializeAtomicAssetsData } from '../../shared/helpers/nft-bytes';
import {
  AtomicAssetsRow,
  NFTAssetPretty,
  SchemaFormat,
} from '../../shared/interfaces/nft.interface';
import { TableResponse, WalletConnection } from '../../shared/interfaces/wallet.interface';

const USER_AUTH_STORAGE_KEY = 'proton-storage-user-auth';
const WALLET_TYPE_STORAGE_KEY = 'proton-storage-wallet-type';
const ASSET_PAGE_SIZE = 100;
const CYPHER_COLLECTION_ID = 'zvapir55jvu4';
@Injectable({
  providedIn: 'root',
})
export class WalletService {
  private session: LinkSession | undefined;
  private link: ProtonWebLink | Link | undefined;
  private schemaCache: Record<string, SchemaFormat> = {};

  readonly walletConnection = signal<WalletConnection | null>(null);
  readonly xprBalance = signal<string>('0.0000 XPR');
  readonly rpc = inject(JSON_RPC);
  readonly nftCollection = signal<NFTAssetPretty[]>([]);
  readonly nftCollectionGrouped = signal<Record<string, NFTAssetPretty[]>>({});
  readonly isLoadingNFTs = signal(false);

  async restoreWalletConnection() {
    const savedConnection = localStorage.getItem(USER_AUTH_STORAGE_KEY);
    console.log('Saved connection from local storage:', savedConnection);
    if (savedConnection) {
      this.walletConnection.set(JSON.parse(savedConnection));
      console.log('Restored wallet connection from local storage:', this.walletConnection());
      try {
        const conn = this.walletConnection();
        const actor = conn?.actor;
        if (!actor) {
          throw new Error('Missing actor in restored wallet connection');
        }
        await this.fetchXprBalance(actor);
        await this.fetchNFTCollection(actor);
      } catch (error) {
        console.error('Failed to restore wallet connection:', error);
        this.disconnectWallet();
      }
    }
  }

  async connectWallet() {
    try {
      const { link, session } = await ConnectWallet({
        linkOptions: {
          endpoints: environment.production ? MAINNET_ENDPOINTS : TESTNET_ENDPOINTS,
          chainId: environment.production ? MAINNET_CHAIN_ID : TESTNET_CHAIN_ID,
        },
        transportOptions: {
          requestAccount: 'tobicasanobi',
        },
        selectorOptions: {
          appName: 'Cypher Syndicate',
        },
      });
      this.session = session;
      this.link = link;
      if (session) {
        const { actor, permission } = session.auth;
        this.walletConnection.set({
          actor: actor.toString(),
          permission: permission.toString(),
        });
        console.log('Connected wallet:', actor.toString());
        await Promise.all([
          this.fetchXprBalance(actor.toString()),
          this.fetchNFTCollection(actor.toString()),
        ]);
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      this.walletConnection.set(null);
    }
  }

  private async fetchXprBalance(account: string): Promise<void> {
    try {
      const response = await this.rpc.get_table_rows({
        code: 'eosio.token',
        table: 'accounts',
        scope: account,
      });
      console.log('XPR Balance:', response.rows[0]);
      if (response && response.rows.length > 0) {
        this.xprBalance.set(response.rows[0].balance);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
    this.xprBalance.set('0.0000 XPR');
  }

  private async getSchemaFormat(collection: string, schemaName: string): Promise<SchemaFormat> {
    const cacheKey = `${collection}:${schemaName}`;
    if (this.schemaCache[cacheKey]) return this.schemaCache[cacheKey];
    const sch = await this.rpc.get_table_rows({
      code: 'atomicassets',
      table: 'schemas',
      scope: collection,
      lower_bound: schemaName,
      upper_bound: schemaName,
      limit: 1,
      json: true,
    });
    const fmt: SchemaFormat = sch?.rows?.[0]?.format ?? [];
    this.schemaCache[cacheKey] = fmt;
    return fmt;
  }

  private async fetchAllAssetRowsByOwner(owner: string): Promise<AtomicAssetsRow[]> {
    const all: AtomicAssetsRow[] = [];
    let lowerBound: string | undefined = undefined;
    while (true) {
      const res: TableResponse<AtomicAssetsRow> = await this.rpc.get_table_rows({
        code: 'atomicassets',
        table: 'assets',
        scope: owner,
        limit: ASSET_PAGE_SIZE,
        lower_bound: lowerBound,
        json: true,
      });
      if (res?.rows?.length) all.push(...res.rows);
      if (!res.more) break;
      lowerBound = res.next_key;
      if (!lowerBound && res.rows?.length) {
        const last = res.rows[res.rows.length - 1];
        lowerBound = typeof last.asset_id === 'string' ? last.asset_id : String(last.asset_id);
      }
    }
    return all;
  }

  async fetchNFTCollection(account: string): Promise<void> {
    this.isLoadingNFTs.set(true);
    console.log('Fetching NFTs for account:', account);
    try {
      const rows = await this.fetchAllAssetRowsByOwner(account);
      const filteredRows = rows.filter((r) => r.collection_name === CYPHER_COLLECTION_ID);
      console.log('NFT fetch response:', rows);
      if (filteredRows && filteredRows.length > 0) {
        const schemaName = filteredRows[0].schema_name;
        const format = await this.getSchemaFormat(CYPHER_COLLECTION_ID, schemaName);
        const assetPromises = filteredRows.map(async (row) => {
          const tmpl = await this.rpc.get_table_rows({
            code: 'atomicassets',
            table: 'templates',
            scope: CYPHER_COLLECTION_ID,
            lower_bound: row.template_id,
            upper_bound: row.template_id,
            limit: 1,
            json: true,
          });
          const data = tmpl.rows[0].immutable_serialized_data
            ? deserializeAtomicAssetsData(format, tmpl.rows[0].immutable_serialized_data)
            : {};
          const asciiHints = Object.values(data)
            .filter((v) => typeof v === 'string')
            .map((v) => String(v));

          return {
            asset_id: String(row.asset_id),
            collection: row.collection_name,
            schema: row.schema_name,
            template_id: String(row.template_id ?? ''),
            name: row.name ?? '(unbenannt)',
            owner: row.owner,
            asciiHints,
            imageUrl: guessImageUrlFromAscii(asciiHints),
            raw: row,
            data,
          };
        });
        const assets: NFTAssetPretty[] = await Promise.all(assetPromises);
        this.nftCollection.set(assets);
        console.log('Filtered NFT assets:', assets);
      } else {
        this.nftCollection.set([]);
      }
    } catch (error) {
      console.error(
        'Failed to fetch NFTs:',
        error instanceof Error ? error.message : 'Unknown error'
      );
      this.nftCollection.set([]);
    } finally {
      this.isLoadingNFTs.set(false);
    }
  }

  disconnectWallet(): void {
    localStorage.removeItem(USER_AUTH_STORAGE_KEY);
    localStorage.removeItem(WALLET_TYPE_STORAGE_KEY);
    this.walletConnection.set(null);
    this.xprBalance.set('--');
    this.nftCollection.set([]);
  }
}

function sanitizeAsciiHint(s: string): string {
  let v = s.trim().replace(/^['"]|['"]$/g, '');
  v = v.replace(/^\.(?=(Qm|baf[1-9A-HJ-NP-Za-km-z]))/, '');
  return v;
}

function guessImageUrlFromAscii(asciiHints: string[]): string | null {
  const hints = asciiHints.map(sanitizeAsciiHint);
  const http = hints.find((s) => /^https?:\/\//i.test(s));
  if (http) return http;
  const ipfsLike = hints.find(
    (s) =>
      /^ipfs:\/\//i.test(s) || /^(Qm[1-9A-HJ-NP-Za-km-z]{44}|baf[1-9A-HJ-NP-Za-km-z]{20,})/.test(s)
  );
  if (ipfsLike) {
    return ipfsToHttp(ipfsLike);
  }
  const imageLike = hints.find((s) => /\b(image|img|thumbnail|thumb)\b/i.test(s));
  if (imageLike) {
    return ipfsToHttp(imageLike);
  }
  return null;
}

function ipfsToHttp(value: string, gateway = 'https://ipfs.io/ipfs/'): string {
  const v = sanitizeAsciiHint(value);
  if (/^https?:\/\//i.test(v)) return v;
  if (/^ipfs:\/\//i.test(v)) return v.replace(/^ipfs:\/\//i, gateway);
  if (/^(Qm[1-9A-HJ-NP-Za-km-z]{44}|baf[1-9A-HJ-NP-Za-km-z]{20,})/.test(v)) {
    return gateway + v;
  }
  return v;
}
